import { useSidebar } from '../contexts/sidebar';
import {
  proxyBannerHeight,
  sideBarWidth,
  spFullContentMinHeight,
  spFullContentPaddingBottom,
  spHeaderHeight,
  spProxyBannerHeight,
} from '../constants/location';
import { gray100 } from '../constants/colors';
import { zIndexLayer } from '../constants/enum';
import { useCompanyId } from './useCompanyId';

interface PageLayoutStyles {
  pageWrapper: Record<string, unknown>;
  sidebar: Record<string, unknown>;
  sidebarCollapsed: Record<string, unknown>;
  pageContent: Record<string, unknown>;
  pageContentCollapsed: Record<string, unknown>;
  pageContentNoFooter: Record<string, unknown>;
  fullHeightContent: Record<string, unknown>;
  header: Record<string, unknown>;
}

export const usePageLayout = (): {
  sideBarCollapsed: boolean;
  toggleSidebar: () => void;
  styles: PageLayoutStyles;
} => {
  const { collapsed: sideBarCollapsed, toggleSidebar } = useSidebar();
  const { isProxyMode } = useCompanyId();

  const styles: PageLayoutStyles = {
    pageWrapper: {
      display: 'flex',
    },
    sidebar: {
      backgroundColor: gray100,
      height: isProxyMode ? `calc(100vh - ${proxyBannerHeight})` : '100vh',
      position: 'fixed',
      top: isProxyMode ? proxyBannerHeight : 0,
      width: sideBarWidth,
      transition: 'width 0.3s ease',
      '@media screen and (max-width:960px)': {
        height: spHeaderHeight,
        width: '100%',
        zIndex: zIndexLayer.sideBarWrapper,
        top: isProxyMode ? spProxyBannerHeight : 0,
      },
    },
    sidebarCollapsed: {
      width: '80px',
      '@media screen and (max-width:960px)': {
        width: '100%',
      },
    },
    pageContent: {
      alignItems: 'flex-start',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      marginLeft: '240px',
      overflow: 'hidden',
      width: '100%',
      transition: 'margin-left 0.3s ease',
      '@media screen and (max-width:960px)': {
        marginLeft: 0,
        marginTop: isProxyMode
          ? `calc(${spHeaderHeight} + ${spHeaderHeight} + ${spProxyBannerHeight})`
          : `calc(${spHeaderHeight} + ${spHeaderHeight})`,
        paddingBottom: '180px',
      },
    },
    pageContentCollapsed: {
      marginLeft: '80px',
      '@media screen and (max-width:960px)': {
        marginLeft: 0,
      },
    },
    // 下部に固定ボタンを持たない画面向け。pageContent の paddingBottom(180px) は
    // 一覧画面の固定ボタン向けの値のため、これらの画面では詰めて不要な縦スクロールを防ぐ。
    pageContentNoFooter: {
      '@media screen and (max-width:960px)': {
        paddingBottom: spFullContentPaddingBottom,
      },
    },
    // 内容を画面の残り領域いっぱいに広げて縦中央寄せするための高さ。
    // SP は代理バナーの有無で pageContent の marginTop が変わるため、
    // ここで isProxyMode を反映させて画面側に固定値を持たせない。
    fullHeightContent: {
      minHeight: `calc(100vh - 80px)`,
      '@media screen and (max-width:960px)': {
        minHeight: spFullContentMinHeight(isProxyMode),
      },
    },
    header: {
      alignItems: 'center',
      boxSizing: 'border-box',
      display: 'flex',
      height: '48px',
      justifyContent: 'space-between',
      padding: '0 32px',
      width: '100%',
      '@media screen and (max-width:960px)': {
        // 固定値にすると、ヘッダーに置いたボタン（44px 等）がはみ出して
        // pageContent の overflow: hidden に切り取られるため、中身の高さに追随させる。
        // タイトルのみの画面は実高 24px 程度のため、minHeight で従来の見た目を保つ。
        height: 'auto',
        minHeight: '28px',
        padding: '0 16px',
      },
    },
  };

  return {
    sideBarCollapsed,
    toggleSidebar,
    styles,
  };
};
